import React, { useState, useEffect } from 'react';
import { DisplayInactiveToggle, FilterGrid, FilterField, type SearchFieldProps } from '@/components/ui';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { AssetFilterOptions } from '../../../services/assetService';
import { ASSET_TYPE_LABELS } from '../../../constants/assetTypes';

export interface AssetFilterValues {
  serviceAreaId: string;
  contractAreaId: string;
  assetType: string;
  /** V3-026: Display Inactive, off by default (INACT-01). */
  displayInactive?: boolean;
}

export const EMPTY_ASSET_FILTERS: AssetFilterValues = {
  serviceAreaId: '',
  contractAreaId: '',
  assetType: '',
  displayInactive: false,
};

interface AssetFiltersProps {
  filterOptions: AssetFilterOptions;
  filters: AssetFilterValues;
  onApplyFilters: (filters: AssetFilterValues) => void;
  onClearFilters?: () => void;
  /** The page's search, shown in the panel's header bar (V2-077). */
  search?: SearchFieldProps;
  /** True when a search is applied: Clear All then has something to clear. */
  searchApplied?: boolean;
}

/** Filters that differ from their default: the three dropdowns, and Display Inactive turned on. */
export const countActiveAssetFilters = (f: AssetFilterValues): number =>
  [f.serviceAreaId, f.contractAreaId, f.assetType].filter((v) => v !== '').length + (f.displayInactive ? 1 : 0);

/**
 * Every filter that hides assets, defaults included (FLT-05): with Display Inactive off, the
 * default hides inactive assets, so the summary still reads "1 filter applied" in grey.
 */
export const countAppliedAssetFilters = (f: AssetFilterValues): number =>
  [f.serviceAreaId, f.contractAreaId, f.assetType].filter((v) => v !== '').length + (f.displayInactive ? 0 : 1);

// Service Area stays single-select: GET /api/assets parses `serviceAreaId` with parseInt
// (server/src/modules/assets/assets.controller.ts:72,89), so it cannot take a list.
const AssetFilters: React.FC<AssetFiltersProps> = ({
  filterOptions,
  filters,
  onApplyFilters,
  onClearFilters,
  search,
  searchApplied = false,
}) => {
  // Pending filters (edited by the controls) vs applied filters (what the server got).
  const [pendingFilters, setPendingFilters] = useState(filters);

  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);

  const activeFilterCount = countActiveAssetFilters(filters);

  const hasUnappliedChanges =
    pendingFilters.serviceAreaId !== filters.serviceAreaId ||
    pendingFilters.contractAreaId !== filters.contractAreaId ||
    pendingFilters.assetType !== filters.assetType ||
    Boolean(pendingFilters.displayInactive) !== Boolean(filters.displayInactive);

  const handlePendingFilterChange = (name: keyof AssetFilterValues, value: string) => {
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setPendingFilters(EMPTY_ASSET_FILTERS);
    onClearFilters?.();
  };

  const assetTypeLabel = (type: string) => ASSET_TYPE_LABELS[type as keyof typeof ASSET_TYPE_LABELS] || type;

  return (
    <FiltersCard
      title="Asset Filters"
      subtitle="Filter assets by service area, contract area, and type"
      collapsible
      defaultCollapsed={false}
      appliedFilterCount={countAppliedAssetFilters(filters)}
      activeFilterCount={activeFilterCount}
      onApply={() => onApplyFilters(pendingFilters)}
      applyDisabled={!hasUnappliedChanges}
      onClear={handleClearFilters}
      hasActiveFilters={activeFilterCount > 0 || searchApplied}
      search={search}
    >
      {/* One select style for the whole row (W-M1): every control is a SearchableDropdown. */}
      <FilterGrid>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.serviceAreas}
            value={pendingFilters.serviceAreaId}
            onChange={(value) => handlePendingFilterChange('serviceAreaId', value)}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            placeholder="All Service Areas"
            className="tw-w-full"
            label="Service Area"
            noOptionsMessage="No service areas available"
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.contractAreas}
            value={pendingFilters.contractAreaId}
            onChange={(value) => handlePendingFilterChange('contractAreaId', value)}
            getValue={(area) => area.id.toString()}
            formatOption={(area) => area.name}
            placeholder="All Contract Areas"
            className="tw-w-full"
            label="Contract Area"
            noOptionsMessage="No contract areas available"
          />
        </FilterField>
        <FilterField>
          <SearchableDropdown
            options={filterOptions.assetTypes}
            value={pendingFilters.assetType}
            onChange={(value) => handlePendingFilterChange('assetType', value)}
            getValue={(type) => type}
            formatOption={assetTypeLabel}
            placeholder="All Types"
            className="tw-w-full"
            label="Asset Type"
            noOptionsMessage="No asset types available"
          />
        </FilterField>
        <FilterField>
          <DisplayInactiveToggle
            id="assets-display-inactive"
            checked={Boolean(pendingFilters.displayInactive)}
            onChange={(checked) => setPendingFilters((prev) => ({ ...prev, displayInactive: checked }))}
          />
        </FilterField>
      </FilterGrid>
    </FiltersCard>
  );
};

export default AssetFilters;
